import { createContext, useContext, ReactNode, JSX } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useQuery, gql } from '@zenbusiness/application-commons-graphql';
import { useError } from '@zenbusiness/application-commons-logging';
import PageError from '@zenbusiness/zui/PageError';
import PageLoading from '@zenbusiness/zui/PageLoading';

import { DB_APPLICATION_STATUS } from '#client/constants/application';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useMfaStatus from '#client/hooks/useMfaStatus';

const APPLICATION_STATUS_WITH_UNIT_ACCOUNT_ID = [DB_APPLICATION_STATUS.APPROVED];

const AccountsDataContext = createContext(null);
AccountsDataContext.displayName = 'AccountsDataContext';

export const ACCOUNTS_DATA_QUERY = gql`
  query bankingAccountsData(
    $businessEntityUuid: UUID!
    $skipPartnerAccounts: Boolean!
    $skipBankingAccounts: Boolean!
  ) {
    partnerAccounts(input: { businessEntityId: $businessEntityUuid })
      @skip(if: $skipPartnerAccounts) {
      partnerAccountIds
    }
    bankingAccounts(businessEntityUuid: $businessEntityUuid) @skip(if: $skipBankingAccounts) {
      id
      partnerCustomerId
    }
  }
`;

/**
 * AccountsDataProvider
 * Fetches and manages partner accounts and banking accounts data and provides it via context.
 *
 * Renders:
 *   - A loading indicator while fetching data
 *   - An error page if fetching fails
 *   - Its children wrapped in AccountsDataContext.Provider when data is ready
 *
 * @param {ReactNode} children - Children to render
 * @returns {JSX.Element} - AccountsDataContext context
 */
function AccountsDataProvider({ children }) {
  const { businessEntityUuid } = useBusinessEntity();
  const { appStatus } = useApplicationStatusContext();
  const { isValidMfa, isEnabled, isFetchingMfaStatus } = useMfaStatus();

  const skipPartnerAccounts =
    isFetchingMfaStatus ||
    !isValidMfa ||
    !isEnabled ||
    !APPLICATION_STATUS_WITH_UNIT_ACCOUNT_ID.includes(appStatus);

  const skipBankingAccounts = appStatus !== DB_APPLICATION_STATUS.APPROVED;

  const request = useQuery(ACCOUNTS_DATA_QUERY, {
    noThrow: true,
    variables: {
      businessEntityUuid,
      skipPartnerAccounts,
      skipBankingAccounts
    },
    skip: skipPartnerAccounts && skipBankingAccounts,
    skipCache: true
  });

  useError(request.error);

  if (request.error) {
    return <PageError />;
  }
  if (request.loading) {
    return <PageLoading />;
  }

  return <AccountsDataContext.Provider value={request}>{children}</AccountsDataContext.Provider>;
}

function useAccountsDataContext() {
  const context = useContext(AccountsDataContext);
  if (!context) {
    throw new Error('useAccountsDataContext must be used within a AccountsDataProvider');
  }
  return context;
}

export { AccountsDataProvider, useAccountsDataContext };
